import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import SectionBadge from '../ui/SectionBadge';
import Button from '../ui/Button';
import { 
  ChampionIcon, 
  ArrowLeft02Icon,
  StarIcon,
  ShieldEnergyIcon,
  Settings01Icon
} from 'hugeicons-react';
import { useMobilePerformance } from '../../hooks/useMobilePerformance';

type PackageCard = {
  id: number;
  name: string;
  description?: string | null;
  price: number;
  badgeLabel: string | null;
  features: string[];
  deliveryConfig: {
    enabled: boolean;
    allowPickup: boolean;
    pickupFee?: number | null;
    allowDropoff: boolean;
    dropoffFee?: number | null;
  };
  serviceId?: number | null;
};

type PricingPackagesProps = {
  packages?: PackageCard[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onOpenBooking: (opts: { package?: PackageCard | null; service?: any }) => void;
};

export default function PricingPackages({
  packages = [],
  loading,
  error,
  onRetry,
  onOpenBooking,
}: PricingPackagesProps) {
  const { shouldReduceAnimations } = useMobilePerformance();

  if (loading) {
    return (
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="animate-pulse text-gray-400">جاري تحميل الباقات...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
        <div className="container mx-auto px-4 text-center text-white space-y-4">
          <div className="text-red-400">{error}</div>
          {onRetry && (
            <Button variant="outline" onClick={onRetry} className="mx-auto">
              إعادة المحاولة
            </Button>
          )}
        </div>
      </section>
    );
  }

  if (!packages || packages.length === 0) {
    return (
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
        <div className="container mx-auto px-4 text-center text-white space-y-2">
          <div className="text-gray-400">لم يتم تفعيل أي باقات حالياً.</div>
          {onRetry && (
            <Button variant="outline" onClick={onRetry} className="mx-auto">
              تحديث
            </Button>
          )}
        </div>
      </section>
    );
  }

  const visible = packages.slice(0, 4);

  return (
    <section
      id="packages"
      className="py-32 relative overflow-hidden bg-[#020000]"
      dir="rtl"
    >
       {/* Cinematic Magma Background Components */}
       <div className="absolute inset-0 pointer-events-none">
          {/* Deep Ambient Base */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_#1a0000_0%,_transparent_70%)] opacity-40" />
          
          {/* Pulsing Heat Orbs */}
          {!shouldReduceAnimations && (
            <>
              <motion.div 
                animate={{ 
                  opacity: [0.1, 0.2, 0.1],
                  scale: [1, 1.2, 1],
                  x: [-20, 20, -20]
                }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-[20%] right-[10%] w-[600px] h-[600px] bg-primary/10 blur-[150px] rounded-full" 
              />
              <motion.div 
                animate={{ 
                  opacity: [0.05, 0.15, 0.05],
                  scale: [1.2, 1, 1.2],
                  y: [-30, 30, -30]
                }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-[10%] left-[5%] w-[800px] h-[800px] bg-primary/5 blur-[180px] rounded-full" 
              />
            </>
          )}

          {/* Tactical Grid Overlay */}
          <div className="absolute inset-0 opacity-[0.03] [background-image:linear-gradient(to_right,#888_1px,transparent_1px),linear-gradient(to_bottom,#888_1px,transparent_1px)] [background-size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_80%)]" />
       </div>

       {/* Top/Bottom Transition Blends */}
       <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-black to-transparent z-20 pointer-events-none" />
       <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black to-transparent z-20 pointer-events-none" />

        {/* Top seamless transition */}
       <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#020000] to-transparent z-10 pointer-events-none" />
       
       <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 relative">
             <SectionBadge text="عناية فائقة للأداء" icon={StarIcon} />

             <motion.h2 
               initial={shouldReduceAnimations ? { opacity: 0.9 } : { opacity: 0, y: 10 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={shouldReduceAnimations ? { duration: 0.15 } : { duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
               className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tighter"
             >
                باقات <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary-light to-primary drop-shadow-[0_0_20px_rgba(220,10,50,0.3)] italic">مصممة لك</span>
             </motion.h2>
             <p className="text-gray-400 text-lg max-w-2xl mx-auto font-medium leading-relaxed opacity-60">
                اختر الباقة التي تناسب تطلعاتك. هندسة دقيقة لجمال يدوم بأرقى المعايير العالمية.
             </p>
          </div>

          {/* Cards Grid - Desktop optimized for 4 columns, Mobile remains scrollable/stackable */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4 xl:gap-6 items-stretch">
               {visible.map((pkg, idx) => (
                  <PricingCard 
                      key={pkg.id} 
                      pkg={pkg} 
                      idx={idx} 
                      isLast={idx === visible.length - 1}
                      onOpenBooking={() => onOpenBooking({ package: pkg, service: null })}
                      shouldReduceAnimations={shouldReduceAnimations}
                  />
               ))}
          </div>
       </div>
    </section>
  );
}

function PricingCard({
  pkg,
  idx,
  isLast = false,
  onOpenBooking,
  shouldReduceAnimations = false,
}: {
  pkg: PackageCard;
  idx: number;
  isLast?: boolean;
  onOpenBooking: () => void;
  shouldReduceAnimations?: boolean;
}) {
    // 3D Tilt Logic
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [10, -10]), { stiffness: 150, damping: 20 });
    const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-10, 10]), { stiffness: 150, damping: 20 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (shouldReduceAnimations) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      const mouseXRel = (e.clientX - rect.left) / width - 0.5;
      const mouseYRel = (e.clientY - rect.top) / height - 0.5;
      mouseX.set(mouseXRel);
      mouseY.set(mouseYRel);
    };

    const handleMouseLeave = () => {
      mouseX.set(0);
      mouseY.set(0);
    };

    const isVip = pkg.badgeLabel?.trim().toUpperCase() === 'VIP';
    
    const features = pkg.features?.length ? pkg.features : [
      'شامل التنظيف والتلميع',
      'حماية أساسية للهيكل',
    ];
    const priceLabel = pkg.price != null ? `${pkg.price}` : 'اتصل للسعر';

    return (
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.1, duration: 0.6 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ 
              rotateX: shouldReduceAnimations ? 0 : rotateX, 
              rotateY: shouldReduceAnimations ? 0 : rotateY,
              perspective: 1000 
            }}
            className={`
                relative flex flex-col w-full min-w-0 rounded-[40px] p-[1.5px]
                transition-transform duration-500 overflow-visible h-full
                ${isVip ? 'bg-gradient-to-b from-primary via-primary/50 to-transparent' : 
                  isLast ? 'bg-gradient-to-b from-white/40 via-white/10 to-transparent' : 
                  'bg-white/10'}
            `}
        >
            {/* Glossy Top Lit Edge */}
            <div className="absolute inset-[1.5px] rounded-[40px] bg-gradient-to-br from-white/10 via-transparent to-black pointer-events-none z-20" />

            <div className={`
              relative flex flex-col h-full rounded-[39px] overflow-hidden backdrop-blur-3xl
              transition-all duration-500 p-6 lg:p-8
              ${isVip 
                ? 'bg-[#080808]/95 group shadow-[0_50px_100px_rgba(220,10,50,0.2)]' 
                : isLast
                ? 'bg-[#0e0e0e]/95 group shadow-[0_40px_80px_rgba(255,255,255,0.05)] border border-white/10'
                : 'bg-[#0c0c0c]/95 group shadow-2xl'
              }
            `}>
                {/* Tactical HUD Markers */}
                <div className="absolute top-6 left-6 flex flex-col items-start gap-1 opacity-20 pointer-events-none">
                   <div className="w-10 h-[1px] bg-white" />
                   <div className="w-6 h-[1px] bg-white" />
                </div>
                
                {/* Advanced Light Leak for VIP */}
                {isVip && (
                  <div className="absolute -top-32 -right-32 w-64 h-64 bg-primary/20 blur-[80px] rounded-full pointer-events-none group-hover:bg-primary/30 transition-colors" />
                )}

                <div className="relative z-10 flex flex-col h-full space-y-6">
                   {/* Header Area */}
                   <div className="flex justify-between items-start gap-4">
                      <div className="space-y-1 text-right">
                         <div className="flex items-center justify-start gap-2 text-right">
                            <span className={`text-[9px] font-black uppercase tracking-[0.3em] ${isVip ? 'text-primary' : isLast ? 'text-white' : 'text-gray-500'}`}>
                               نظام فئة {idx + 1}
                            </span>
                         </div>
                         <h3 className="text-xl lg:text-2xl font-black text-white uppercase tracking-tight leading-tight">{pkg.name}</h3>
                      </div>
                      
                      <div className="shrink-0">
                        {isVip ? (
                          <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl bg-primary shadow-[0_0_20px_rgba(220,10,50,0.5)] flex items-center justify-center text-white">
                             <ChampionIcon size={24} />
                          </div>
                        ) : (
                          <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-500 group-hover:text-white transition-colors">
                             <ShieldEnergyIcon size={20} />
                          </div>
                        )}
                      </div>
                   </div>

                   {/* Price Core - The Chrome/Metal Look */}
                   <div className="relative py-2 text-right">
                      <div className="flex items-baseline justify-start gap-2">
                         <span className={`text-4xl lg:text-6xl font-black tracking-tighter ${
                           isVip 
                            ? 'text-transparent bg-clip-text bg-gradient-to-b from-white via-[#ff1e1e] to-primary drop-shadow-[0_4px_15px_rgba(220,10,50,0.4)]' 
                            : 'text-white'
                         }`}>
                           {priceLabel}
                         </span>
                         {pkg.price && <span className="text-gray-600 font-black text-lg">د.ل</span>}
                      </div>
                      <div className="flex justify-start">
                        <div className="h-0.5 w-12 bg-primary/30 mt-3 rounded-full" />
                      </div>
                   </div>

                   {/* Strategic Features List */}
                   <div className="flex-grow space-y-5 pt-2">
                      <div className="flex items-center justify-start gap-3">
                         <span className="text-[9px] font-black uppercase tracking-[0.3em] text-gray-500">تفاصيل الخدمات المتضمنة</span>
                         <Settings01Icon size={12} className="text-primary-light" />
                      </div>
                      <ul className="space-y-4">
                          {features.map((feat, i) => (
                              <li key={i} className="flex items-start justify-start gap-3" dir="rtl">
                                  <div className="relative shrink-0 mt-1">
                                     <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                                       isVip ? 'bg-primary/20 border border-primary/40' : 'bg-white/5 border border-white/10'
                                     }`}>
                                        <div className={`w-1 h-1 rounded-full ${isVip ? 'bg-primary' : 'bg-gray-600'}`} />
                                     </div>
                                  </div>
                                  <span className="text-xs lg:text-sm font-bold text-gray-300 tracking-tight leading-relaxed text-right">{feat}</span>
                              </li>
                          ))}
                      </ul>
                   </div>

                   {/* Action Portal - Ignition Button */}
                   <div className="pt-6">
                      <Button 
                          onClick={onOpenBooking}
                          className={`
                            w-full h-12 lg:h-14 rounded-xl lg:rounded-2xl font-black text-[10px] lg:text-xs uppercase tracking-[0.1em] relative overflow-hidden
                            transition-all duration-500 hover:scale-[1.03] active:scale-[0.98]
                            ${isVip 
                              ? 'bg-primary text-white shadow-[0_15px_30px_rgba(220,10,50,0.3)] hover:shadow-primary/50' 
                              : isLast
                              ? 'bg-white text-black shadow-[0_15px_30px_rgba(255,255,255,0.1)] hover:bg-gray-200'
                              : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                            }
                          `}
                      >
                          <span className="relative z-10 flex items-center justify-center gap-2">
                             {isVip ? 'تأكيد واختيار' : 'بدء عملية الحجز'}
                             <ArrowLeft02Icon size={16} className="group-hover:-translate-x-1 transition-transform" />
                          </span>
                          
                          {/* Inner Shine Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent pointer-events-none" />
                      </Button>
                   </div>
                </div>
            </div>

            {/* Float Badge for VIP */}
            {isVip && (
              <motion.div 
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-3 -right-1 z-30"
              >
                  <div className="bg-[#ff1e1e] text-white px-4 py-1.5 rounded-full text-[8px] font-black uppercase tracking-[0.2em] shadow-[0_10px_20px_rgba(220,10,50,0.4)] border border-white/20">
                    VIP مـتـمـيـز
                  </div>
              </motion.div>
            )}
        </motion.div>
    );
}
